import {
  BusinessRole,
  EndMarketRole,
  MaterialGroup,
} from "fe-shared/src/types/domain";
import { FileInterface } from "fe-shared/src/types/common";
import {
  TransactionType,
  TransactionStatus,
  LinkedTransaction,
} from "./transaction";
import {
  TransactionMaterial,
  DispositionFormMaterial,
  TransactionTraversedMaterial,
} from "./materials";

export enum CountryCode {
  Canada = "CA",
  USA = "US",
  Other = "OH",
}

export enum TransactionSection {
  Locations = "Locations",
  Participants = "Participants",
  Data = "Data",
}

export enum TransactionSubSection {
  CommonData = "CommonData",
  RegulatoryYearOverridden = "RegulatoryYearOverridden",
}

export enum TransactionEventType {
  Initiated = "TransactionInitiated",
  PhotoAdded = "PhotoAdded",
  Completed = "TransactionCompleted",
  QrCodeScanned = "QrCodeScanned",
  Created = "Created",
}

export enum WarehouseTypeCode {
  RegisteredCollectionSite = "RegisteredCollectionSite",
  UnregisteredCollectionSite = "UnregisteredCollectionSite",
  HaulerVehicle = "HaulerVehicle",
  HaulerStorageYard = "HaulerStorageYard",
  ProcessorStorageYard = "ProcessorStorageYard",
  ProcessorVehicle = "ProcessorVehicle",
  ProcessingFacility = "ProcessingFacility",
  EndMarket = "EndMarket",
}

export interface TransactionFlagLink {
  transactionNumber: string;
  category: string;
  transactionTypeName: string;
}

export interface TransactionFlag {
  sectionCode: TransactionSection;
  subSectionCode?: TransactionSubSection;
  text: string;
  links?: TransactionFlagLink[];
  isManual: boolean;
  isVisibleToParticipant: boolean;
}

export interface Assignee {
  userId: string | number;
  displayName: string | null;
}

export interface TransactionUser {
  userId: number;
  email: string | null;
  displayName: string | null;
}

export interface Address {
  addressId: number;
  countryCode: CountryCode;
  provinceCode: string;
  stateCode: string;
  otherCountry: string;
  postalOrZip: string;
  city: string;
  addressLine1: string;
  addressLine2?: string;
  latitude: number;
  longitude: number;
}

export interface TransactionSigner {
  companyId: number;
  userId: number | null;
  displayName: string | null;
  signatureImageUrl: string | null;
}

export interface TransactionParticipant {
  companyId: number;
  phone: string;
  regNumber: number;
  displayName: string;
  businessRoleCode: BusinessRole;
  businessAddress: Address;
}

export interface TransactionWarehouseLocation {
  id: number;
  address: Address;
}

export interface TransactionWarehouse {
  warehouseId: number;
  location?: TransactionWarehouseLocation;
  signers: TransactionSigner[];
  participant: TransactionParticipant;
  warehouseTypeCode: WarehouseTypeCode;
  isCompanyActive?: boolean;
  isActive?: boolean;
}

export interface ScaleTicket {
  ticketId?: number | null;
  sortOrder?: number | null;
  createdDate: string;
  scaleTicketDate?: string | null;
  ticket: string | null;
  ticketNumber: string;
}

export interface TransactionEvent {
  eventType: TransactionEventType;
  createdDate: string;
  latitude: number;
  longitude: number;
}

export interface CheckFlagResponse {
  type: string;
  title: string;
  status: number;
  detail: string;
  instance: string;
}

export interface OriginalValues {
  materials: TransactionMaterial[];
  grossWeight: number;
  tareWeight: number;
}

export interface DocumentsResponse {
  description: string;
  documentPath: string;
  name: string;
  size: number;
}

export interface DispositionInvoice {
  date: string;
  number: string;
}

export interface Transaction {
  transactionId: number;
  transactionNumber: string;
  transactionType: TransactionType;
  statusCode: TransactionStatus;
  syncedDate?: string;
  submittedDate?: string;
  completedDate?: string;
  actualTransactionDate?: string;
  submittedBy?: TransactionUser | null;
  invalidatedBy?: TransactionUser | null;
  invalidationDate: string | null;
  invalidationReason: string | null;
  isVisibleInvalidationReasonParticipant: boolean;
  assignee?: Assignee | null;
  events: TransactionEvent[];
  sourceWarehouse?: TransactionWarehouse;
  targetWarehouse?: TransactionWarehouse;
  materials: TransactionMaterial[];
  traversedMaterials?: TransactionTraversedMaterial[] | null;
  materialAttributes: string[];
  comment: string;
  grossWeight: number;
  tareWeight: number;
  transactionFlags: TransactionFlag[];
  materialPhotos: string[];
  paperFormImages?: string[];
  scaleTickets: ScaleTicket[];
  linkedTransactions: LinkedTransaction[];
  isAdjustable: boolean;
  isAutoApproved: boolean;
  isValidating: boolean;
  isCompletelyInitialDelivery: boolean;
  adjustmentReasons?: string[];
  adjustmentOtherReasonComment?: string;
  originalValues?: OriginalValues;
  attachments?: DocumentsResponse[];
  invoiceNumber?: string;
  requestNumber?: string | null;
  isPaperForm: boolean;
  dispositionInvoice?: DispositionInvoice;
  regulatoryYear?: string | null;
  claimNumber?: string | null;
  trailerNumber?: string | null;
  isCreatedOnWeb?: boolean;
  processingOperationCode?: string;
  processingStatusCode?: string;
  processingFailureReason?: string;
  isInClosurePeriod: boolean;
  paymentAmount?: number | null;
  estimatedNonEligibleBuffer?: number;
  bufferAllocationPeriodDurationInMinutes?: number | null;
  targetCompanyInfo?: {
    companyId: number;
    displayName: string;
    phone?: string;
    regNumber?: number;
    businessRoleCode?: string;
    isCompanyActive?: boolean;
    businessAddress?: {
      addressId?: number;
      city?: string;
      stateCode?: string;
      provinceCode?: string;
      addressLine1?: string;
      postalOrZip?: string;
      countryCode?: CountryCode;
      latitude?: number;
      longitude?: number;
    };
  };
}

export interface Retreader
  extends Omit<Transaction, "materialPhotos" | "scaleTickets" | "isPaperForm"> {
  collectionInfoMaterials: TransactionMaterial[];
  recoveryInfoMaterials: TransactionMaterial[];
  retreadRepairInfoMaterials: TransactionMaterial[];
  recoveryGain: number;
  isKnownRetreaderTiresActualWeight: boolean;
  retreadingPeriodDate: string;
  productionReportDate: string;
}

export interface EditDispositionFormContent extends Record<string, any> {
  endMarketRoleCode: EndMarketRole;
  dispositionCompanyName: string;
  materialGroups: MaterialGroup<DispositionFormMaterial>[];
  attachments: FileInterface[];
}

export interface EditDispositionParams extends EditDispositionFormContent {
  transactionId: number;
  businessRoleCode: BusinessRole;
}
